/**
 * GitUserMapSection — admin panel to map a Git identity (email and/or username,
 * per Git server) to a juuplan user. The reconcile CREATE tier uses these
 * mappings to ASSIGN an auto-created task (for already-done Git work) to the
 * person who did it. The Git identity is used only server-side and never sent
 * to the AI.
 *
 * Style constants mirror GitAdminSection / AiSettingsSection (there is no shared
 * <Input>/<Button> component in this codebase — Tailwind class strings + CSS
 * variable tokens are the convention).
 */
import { useEffect, useState } from "preact/hooks";
import { Plus, Trash2 } from "lucide-preact";
import {
  listGitServers,
  listGitUserMaps,
  listAdminUsers,
  createGitUserMap,
  deleteGitUserMap,
  type AdminGitServer,
  type AdminGitUserMap,
  type AdminUser,
} from "../../lib/api";
import { toastError, toastSuccess } from "../../lib/toast";

const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";
const LABEL_CLS =
  "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";
const BTN_PRIMARY =
  "inline-flex items-center gap-1 rounded bg-[color:var(--color-primary)] px-3 py-2 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 transition-colors";
const BTN_DANGER =
  "rounded bg-[color:var(--color-error)] px-2 py-1 text-xs text-[color:var(--color-on-error)] hover:opacity-90 transition-opacity";

export function GitUserMapSection(): preact.JSX.Element {
  const [servers, setServers] = useState<AdminGitServer[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [maps, setMaps] = useState<AdminGitUserMap[]>([]);
  const [serverId, setServerId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // add-form state
  const [formUserId, setFormUserId] = useState<number | null>(null);
  const [formEmail, setFormEmail] = useState("");
  const [formUsername, setFormUsername] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [srv, usr] = await Promise.all([listGitServers(), listAdminUsers()]);
        setServers(srv);
        setUsers(usr.filter((u) => u.is_active));
        setServerId(srv[0]?.id ?? null);
      } catch {
        setError("Konnte Server/Benutzer nicht laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Reload mappings whenever the selected server changes.
  useEffect(() => {
    if (serverId == null) {
      setMaps([]);
      return;
    }
    void (async () => {
      try {
        setMaps(await listGitUserMaps(serverId));
      } catch {
        toastError("Konnte Zuordnungen nicht laden.");
      }
    })();
  }, [serverId]);

  async function handleAdd(): Promise<void> {
    if (serverId == null || formUserId == null) {
      toastError("Bitte Server und Benutzer wählen.");
      return;
    }
    if (!formEmail.trim() && !formUsername.trim()) {
      toastError("E-Mail oder Benutzername angeben.");
      return;
    }
    setSaving(true);
    try {
      const created = await createGitUserMap({
        server_id: serverId,
        user_id: formUserId,
        ...(formEmail.trim() ? { git_email: formEmail.trim() } : {}),
        ...(formUsername.trim() ? { git_username: formUsername.trim() } : {}),
      });
      setMaps((prev) => [...prev, created]);
      setFormEmail("");
      setFormUsername("");
      setFormUserId(null);
      toastSuccess("Zuordnung angelegt.");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Anlegen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number): Promise<void> {
    try {
      await deleteGitUserMap(id);
      setMaps((prev) => prev.filter((m) => m.id !== id));
      toastSuccess("Zuordnung entfernt.");
    } catch {
      toastError("Entfernen fehlgeschlagen.");
    }
  }

  return (
    <div>
      <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] mb-1">
        GitLab-Benutzer → juuplan-Benutzer
      </h2>
      <p class="text-sm text-[color:var(--color-on-surface-variant)] mb-4">
        Ordnet eine Git-Identität (E-Mail und/oder Benutzername) einem
        juuplan-Benutzer zu. Beim automatischen Anlegen bereits erledigter
        GitLab-Arbeit wird die Aufgabe dieser Person zugewiesen. Die Git-Identität
        wird nur serverseitig verwendet und nie an die KI gesendet.
      </p>

      {loading ? (
        <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      ) : error ? (
        <p class="text-sm text-[color:var(--color-error)]">{error}</p>
      ) : servers.length === 0 ? (
        <p class="text-sm italic text-[color:var(--color-on-surface-variant)]">
          Noch kein Git-Server konfiguriert.
        </p>
      ) : (
        <div class="space-y-4">
          <div>
            <label class={LABEL_CLS} for="gum-server">
              Git-Server
            </label>
            <select
              id="gum-server"
              class={SELECT_CLS}
              value={serverId ?? undefined}
              onChange={(e) =>
                setServerId(Number((e.target as HTMLSelectElement).value))
              }
            >
              {servers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.provider})
                </option>
              ))}
            </select>
          </div>

          {maps.length === 0 ? (
            <p class="text-sm italic text-[color:var(--color-on-surface-variant)]">
              Noch keine Zuordnungen für diesen Server.
            </p>
          ) : (
            <ul class="divide-y divide-[color:var(--color-outline-variant)] rounded-lg border border-[color:var(--color-outline-variant)]">
              {maps.map((m) => (
                <li key={m.id} class="flex items-center justify-between gap-3 px-3 py-2">
                  <div class="min-w-0">
                    <div class="text-sm text-[color:var(--color-on-surface)] truncate">
                      {m.git_email && <span>{m.git_email}</span>}
                      {m.git_email && m.git_username && (
                        <span class="text-[color:var(--color-on-surface-variant)]"> · </span>
                      )}
                      {m.git_username && (
                        <span class="text-[color:var(--color-on-surface-variant)]">
                          @{m.git_username}
                        </span>
                      )}
                    </div>
                    <div class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
                      → {m.user_name ?? `User #${m.user_id}`}
                      {m.user_email ? ` (${m.user_email})` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    class={BTN_DANGER}
                    aria-label="Zuordnung löschen"
                    onClick={() => void handleDelete(m.id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* add-form */}
          <div class="rounded-lg border border-[color:var(--color-outline-variant)] p-4 space-y-3">
            <h3 class="text-sm font-medium text-[color:var(--color-on-surface)]">
              Zuordnung hinzufügen
            </h3>
            <div>
              <label class={LABEL_CLS} for="gum-user">
                juuplan-Benutzer
              </label>
              <select
                id="gum-user"
                class={SELECT_CLS}
                value={formUserId ?? ""}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  setFormUserId(v === "" ? null : Number(v));
                }}
              >
                <option value="">— wählen —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class={LABEL_CLS} for="gum-email">
                  Git-E-Mail
                </label>
                <input
                  id="gum-email"
                  class={INPUT_CLS}
                  type="email"
                  placeholder="dev@example.com"
                  value={formEmail}
                  onInput={(e) => setFormEmail((e.target as HTMLInputElement).value)}
                />
              </div>
              <div>
                <label class={LABEL_CLS} for="gum-username">
                  Git-Benutzername
                </label>
                <input
                  id="gum-username"
                  class={INPUT_CLS}
                  type="text"
                  placeholder="octocat"
                  value={formUsername}
                  onInput={(e) => setFormUsername((e.target as HTMLInputElement).value)}
                />
              </div>
            </div>
            <p class="text-xs text-[color:var(--color-on-surface-variant)]">
              Mindestens eines von E-Mail / Benutzername ist erforderlich. GitLab
              liefert bei MRs/Issues meist den Benutzernamen, bei Commits die
              E-Mail.
            </p>
            <button
              type="button"
              class={BTN_PRIMARY}
              disabled={saving}
              onClick={() => void handleAdd()}
            >
              <Plus size={14} /> Hinzufügen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
